import { IController } from 'angular';
import { each, filter, map, size, startsWith } from 'lodash';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { IManageUsersUser } from './../manage-users.controller';

function sortString(s1, s2) {
  const s1lowerCase = s1.toLowerCase();
  const s2lowerCase = s2.toLowerCase();

  return s1lowerCase > s2lowerCase ? 1 : -1;
}

interface IAccessLevelHumanFilter {
  'Delegated Admin': boolean;
  'No Access': boolean;
  'School Admin': boolean;
  'School Wide Editor': boolean;
  'Caseload Editor': boolean;
  'School Wide Viewer': boolean;
  'Caseload Viewer': boolean;
}

interface IAccessLevelKeyFilter {
  delegatedAdmin: boolean;
  noAccess: boolean;
  editAll: boolean;
  editCaseload: boolean;
  viewAll: boolean;
  viewCaseload: boolean;
  schoolAdmin: boolean;
}

interface INewUserFilter {
  name?: string;
  jobRole?: string;
  googleEmail?: boolean;
  accessLevel?: IAccessLevelHumanFilter;
}

export class ManageUsersFiltersCtrl implements IController {
  static $inject: string[] = ['UtilitiesService'];

  // bindings
  users: IManageUsersUser[];

  // class properties
  private accessLevelFilter: IAccessLevelKeyFilter;
  private userNamesVals: string[];
  private usersCopy: IManageUsersUser[];
  public filteredUserNames: string[];
  public userNameFilter: string;
  public userJobRoleFilter: string;
  public userAssignmentDescriptionFilter: string;

  constructor(private utilitiesService: UtilitiesService) {
    //
  }

  $onInit() {
    // access level checkboxes checked
    this.accessLevelFilter = {
      delegatedAdmin: true,
      noAccess: true,
      editAll: true,
      editCaseload: true,
      viewAll: true,
      viewCaseload: true,
      schoolAdmin: true,
    };

    // set these values on "user" $onChanges also
    this.usersCopy = this.utilitiesService.copyPOJO(this.users);
    this.userNamesVals = this.getUserNamesVals(this.usersCopy);
    this.filteredUserNames = this.getFilteredUserNames(null);
  }

  $onChanges(changesObj) {
    const { users } = changesObj;
    const { currentValue, previousValue } = users;

    if (size(previousValue) && currentValue !== previousValue) {
      this.usersCopy = this.utilitiesService.copyPOJO(this.users);
      this.userNamesVals = this.getUserNamesVals(this.usersCopy);
      this.filteredUserNames = this.getFilteredUserNames(this.userNameFilter);
    }
  }

  public getFilteredUserNames(userNameFilterVal): string[] {
    let filteredUserNames;

    if (!userNameFilterVal) {
      filteredUserNames = this.userNamesVals;
    } else {
      filteredUserNames = filter(this.userNamesVals, v => {
        return this.hasNameFilterVal(v, userNameFilterVal);
      });
    }

    return filteredUserNames;
  }

  public setFilteredUserNames(userNameFilterVal): void {
    this.filteredUserNames = this.getFilteredUserNames(userNameFilterVal);
  }

  public filterUsers = this.filterUsersPartial().bind(this);

  private getUserNamesVals(users: IManageUsersUser[]): string[] {
    const userNames = map(users, u => u.name).sort(sortString);

    return userNames;
  }

  private filterUsersPartial(): Function {
    const accessLevel: IAccessLevelHumanFilter = {
      'Delegated Admin': true,
      'No Access': true,
      'School Admin': true,
      'School Wide Editor': true,
      'Caseload Editor': true,
      'School Wide Viewer': true,
      'Caseload Viewer': true,
    };
    let allFilters: any = {
      name: '',
      jobRole: '',
      assignmentDescriptions: '',
      accessLevel,
    };

    const filterUsers = (filters: INewUserFilter) => {
      allFilters = this.utilitiesService.deepMergeObjs(allFilters, filters);

      const users = this.users;
      const nameFilter = allFilters.name;
      const accessLevelFilter = allFilters.accessLevel;

      const filteredUsers: IManageUsersUser[] = each(users, user => {
        let filtered = true;

        if (nameFilter) {
          filtered = this.hasNameFilterVal(user.name, nameFilter);
        }

        if (filtered && accessLevelFilter) {
          filtered = this.hasAccessLevelFilterVal(user, accessLevelFilter);
        }

        user.filtered = filtered;
      });

      return filteredUsers;
    };

    return filterUsers;
  }

  private hasNameFilterVal(userName: string, nameFilterValue: string): boolean {
    const lowerCaseName = userName ? userName.toLowerCase() : '';
    const name = lowerCaseName.replace(',', '');
    const reversed = lowerCaseName
      .split(', ')
      .reverse()
      .join(' ')
      .replace(',', '');

    const lowerCaseFilterName = nameFilterValue ? nameFilterValue.toLowerCase() : '';
    const filterNameFormatted = lowerCaseFilterName.replace(',', '');
    const filterNameReversed = lowerCaseFilterName
      .split(', ')
      .reverse()
      .join(' ')
      .replace(',', '');

    const startsWithFilter = startsWith(name, filterNameFormatted) || startsWith(reversed, filterNameReversed);

    return startsWithFilter;
  }

  private hasAccessLevelFilterVal(user: IManageUsersUser, accessLevelFilter: IAccessLevelHumanFilter): boolean {
    return accessLevelFilter[user.accessLevel] === true;
  }
}
